import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { BookOpen, Lock, TrendingUp, TrendingDown, DollarSign, FileText, BarChart3, ArrowRight } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#0A2B1D', '#2D6A4F', '#52B788', '#74C69D', '#95D5B2', '#B7E4C7', '#D8F3DC'];

const fmt = (n) => `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct = (n) => `${(n || 0).toFixed(1)}%`;

const ClientPortalView = () => {
  const { shareToken, magicToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [pwLoading, setPwLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let res;
        if (magicToken) {
          res = await axios.get(`${API}/api/mybookie/client-portal/magic/${magicToken}`);
        } else {
          res = await axios.get(`${API}/api/mybookie/client-portal/view/${shareToken}`);
        }
        if (res.data.requires_password) {
          setNeedsPassword(true);
        } else {
          setData(res.data);
        }
      } catch (err) {
        console.error("ClientPortal load error:", err);
        setError('This portal link is invalid, expired, or has been disabled by the owner.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [shareToken, magicToken]);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPwLoading(true);
    try {
      const res = await axios.post(`${API}/api/mybookie/client-portal/verify-password/${shareToken}`, { password });
      setData(res.data);
      setNeedsPassword(false);
    } catch (err) {
      console.error("ClientPortal password error:", err);
      setError('Incorrect password');
    } finally {
      setPwLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F6F5F2]">
        <div className="animate-spin w-8 h-8 border-2 border-[#0A2B1D] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (needsPassword) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F6F5F2]" data-testid="portal-password-gate">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <Lock className="w-12 h-12 text-[#0A2B1D] mx-auto mb-3" />
              <h2 className="text-xl font-semibold text-[#111614]">Password Required</h2>
              <p className="text-sm text-[#5C6661] mt-1">Enter the password to view this financial dashboard</p>
            </div>
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter portal password"
                required
                data-testid="portal-pw-input"
              />
              {error && <p className="text-red-500 text-sm">{error}</p>}
              <Button type="submit" disabled={pwLoading} className="w-full bg-[#0A2B1D] hover:bg-[#143D2B]" data-testid="portal-pw-submit">
                {pwLoading ? 'Verifying...' : 'Access Portal'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F6F5F2]">
        <Card className="max-w-md mx-4">
          <CardContent className="p-8 text-center">
            <Lock className="w-12 h-12 text-[#8A948F] mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-[#111614] mb-2">Access Denied</h2>
            <p className="text-[#5C6661]">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) return null;

  const { business, summary, monthly_data, expense_categories, invoice_summary, forecast, recent_income, recent_expenses } = data;

  return (
    <div className="min-h-screen bg-[#F6F5F2]" data-testid="client-portal-view">
      {/* Header */}
      <header className="bg-white border-b border-[#E6E8E6] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-lg font-semibold text-[#111614]">{business?.name}</h1>
            {business?.type && <Badge variant="outline" className="text-xs">{business.type}</Badge>}
          </div>
          <div className="flex items-center gap-3">
            <Badge className="bg-[#E2ECE7] text-[#0A2B1D]">Read-Only</Badge>
            <div className="flex items-center gap-1.5">
              <div className="w-7 h-7 bg-[#0A2B1D] rounded flex items-center justify-center">
                <BookOpen className="w-4 h-4 text-white" />
              </div>
              <span className="text-sm font-medium text-[#5C6661]">MyBookie</span>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        {/* Year Badge */}
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-[#111614]">Financial Overview</h2>
          <Badge variant="outline" className="text-sm">{data.year}</Badge>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="portal-summary-cards">
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-green-600" />
                <span className="text-xs text-[#5C6661] uppercase tracking-wide">Revenue</span>
              </div>
              <p className="text-2xl font-bold text-green-600">{fmt(summary.total_income)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <TrendingDown className="w-4 h-4 text-red-500" />
                <span className="text-xs text-[#5C6661] uppercase tracking-wide">Expenses</span>
              </div>
              <p className="text-2xl font-bold text-red-500">{fmt(summary.total_expenses)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-4 h-4 text-[#0A2B1D]" />
                <span className="text-xs text-[#5C6661] uppercase tracking-wide">Net Income</span>
              </div>
              <p className={`text-2xl font-bold ${summary.net_income >= 0 ? 'text-[#0A2B1D]' : 'text-red-500'}`}>
                {fmt(summary.net_income)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className="w-4 h-4 text-[#2D6A4F]" />
                <span className="text-xs text-[#5C6661] uppercase tracking-wide">Margin</span>
              </div>
              <p className="text-2xl font-bold text-[#2D6A4F]">{pct(summary.profit_margin)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Monthly Revenue vs Expenses Chart */}
        {monthly_data?.length > 0 && (
          <Card>
            <CardHeader><CardTitle className="text-base">Monthly Revenue vs. Expenses</CardTitle></CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthly_data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E6E8E6" />
                    <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#5C6661' }} />
                    <YAxis tick={{ fontSize: 12, fill: '#5C6661' }} tickFormatter={(v) => `$${v >= 1000 ? (v/1000).toFixed(0) + 'k' : v}`} />
                    <Tooltip formatter={(v) => fmt(v)} />
                    <Bar dataKey="income" fill="#2D6A4F" name="Revenue" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="expenses" fill="#E76F51" name="Expenses" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Expense Breakdown Pie Chart */}
          {expense_categories?.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Expense Breakdown</CardTitle></CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={expense_categories} dataKey="amount" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                        {expense_categories.map((_, idx) => (
                          <Cell key={`cell-${idx}`} fill={COLORS[idx % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => fmt(v)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Invoice Status */}
          {invoice_summary && (
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4" /> Invoice Status</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="p-3 bg-[#F6F5F2] rounded-lg text-center">
                    <p className="text-xs text-[#5C6661]">Total Invoiced</p>
                    <p className="text-lg font-bold text-[#111614]">{fmt(invoice_summary.total_invoiced)}</p>
                  </div>
                  <div className="p-3 bg-[#F6F5F2] rounded-lg text-center">
                    <p className="text-xs text-[#5C6661]">Collected</p>
                    <p className="text-lg font-bold text-green-600">{fmt(invoice_summary.total_collected)}</p>
                  </div>
                </div>
                <div className="flex gap-3 flex-wrap">
                  <Badge className="bg-green-100 text-green-800">{invoice_summary.paid} Paid</Badge>
                  <Badge className="bg-yellow-100 text-yellow-800">{invoice_summary.pending} Pending</Badge>
                  <Badge className="bg-red-100 text-red-800">{invoice_summary.overdue} Overdue</Badge>
                  <Badge variant="outline">{invoice_summary.total} Total</Badge>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Cash Flow Forecast */}
        {forecast?.length > 0 && (
          <Card>
            <CardHeader><CardTitle className="text-base">Cash Flow Forecast (3-Month Projection)</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {forecast.map((f) => (
                  <div key={f.month} className="p-4 bg-[#F6F5F2] rounded-lg">
                    <p className="text-sm font-medium text-[#5C6661] mb-2">{f.month}</p>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between"><span className="text-[#5C6661]">Projected Revenue</span><span className="text-green-600 font-medium">{fmt(f.projected_income)}</span></div>
                      <div className="flex justify-between"><span className="text-[#5C6661]">Projected Expenses</span><span className="text-red-500 font-medium">{fmt(f.projected_expenses)}</span></div>
                      <div className="flex justify-between border-t pt-1 mt-1"><span className="text-[#111614] font-medium">Projected Net</span><span className={`font-bold ${f.projected_net >= 0 ? 'text-[#0A2B1D]' : 'text-red-500'}`}>{fmt(f.projected_net)}</span></div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Transactions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader><CardTitle className="text-base text-green-700">Recent Income ({recent_income?.length || 0})</CardTitle></CardHeader>
            <CardContent>
              {recent_income?.length > 0 ? (
                <div className="space-y-2">
                  {recent_income.map((inc, i) => (
                    <div key={`inc-${inc.date}-${inc.description}-${i}`} className="flex items-center justify-between py-2 border-b border-[#E6E8E6] last:border-0">
                      <div>
                        <p className="text-sm font-medium text-[#111614]">{inc.description}</p>
                        <p className="text-xs text-[#5C6661]">{inc.date}</p>
                      </div>
                      <span className="text-sm font-semibold text-green-600">{fmt(inc.amount)}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-[#5C6661] text-center py-4">No recent income</p>}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base text-red-600">Recent Expenses ({recent_expenses?.length || 0})</CardTitle></CardHeader>
            <CardContent>
              {recent_expenses?.length > 0 ? (
                <div className="space-y-2">
                  {recent_expenses.map((exp, i) => (
                    <div key={`exp-${exp.date}-${exp.description}-${i}`} className="flex items-center justify-between py-2 border-b border-[#E6E8E6] last:border-0">
                      <div>
                        <p className="text-sm font-medium text-[#111614]">{exp.description}</p>
                        <p className="text-xs text-[#5C6661]">{exp.date} {exp.vendor ? `- ${exp.vendor}` : ''}</p>
                      </div>
                      <span className="text-sm font-semibold text-red-500">{fmt(exp.amount)}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-[#5C6661] text-center py-4">No recent expenses</p>}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#111614] text-[#8A948F] py-4 px-6 text-center text-xs mt-12">
        Powered by <span className="text-white font-medium">MyBookie</span> &middot; Read-only Client Portal &middot; {new Date().getFullYear()}
      </footer>
    </div>
  );
};

export default ClientPortalView;
